import React, { useEffect, useMemo, useState } from 'react';
import {
  CardMedia,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import PageContainer from 'src/components/container/PageContainer';

import { t } from 'i18next';
import { useLazyGetTournamentDetailsQuery } from 'src/services/tournaments';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'src/store/Store';
import { setSelectedTournament } from 'src/store/apps/tournament/TournamentSlice';
import Details from './components/Details';
import BlankCard from 'src/components/shared/BlankCard';
import profilecover from 'src/assets/images/backgrounds/profilebg.jpg';
import TournamentTabs from './components/TournamentTabs';
import TournamentOrganizers from 'src/views/pages/tournaments/components/tournamentOrganizers/TournamentOrganizers';
import TournamentTeams from 'src/views/pages/tournaments/components/tournamentTeams/TournamentTeams';
import Spinner from 'src/views/spinner/Spinner';
import TournamentStandings from '../Standings/TournamentStandings';
import Schedule from '../Schedule/Schedule';
import Knockouts from '../Knockouts/Knockouts';
import ApprovalQueue from './components/ApprovalQueue';
import { IconEdit, IconShare, IconTrash } from '@tabler/icons';
import DeleteAlert from 'src/components/shared/DeleteAlert';
import AddEditTournamentForm from '../AddEditTournamentForm/AddEditTournamentForm';
import useTournament from '../../hooks/useTournament';
import { Tournament, TournamentsStatus } from 'src/services/tournaments/getTournaments';
import ExtraMatches from '../ExtraMatches/ExtraMatches';
import toast from 'react-hot-toast';
import TournamentPositions from '../TournamentPositions/TournamentPositions';
import useDynamicLinks from 'src/hooks/useDynamicLinks';
import { LinkType } from 'src/services/links/generateLink';
import useLang from 'src/hooks/useLang';
import { usePermissions } from 'src/hooks/usePermissions';
import { FederationsPermissions } from 'src/types/apps/permissions';

export enum TournamentTabsEnum {
  DETAILS = 'details',
  ORGANIZERS = 'organizers',
  TEAMS = 'teams',
  SCHEDULE = 'schedule',
  STANDINGS = 'standings',
  KNOCKOUTS = 'knockouts',
  EXTRA_MATCHES = 'extra-matches',
  POSITIONS = 'positions',
}

const isWidget = window.location.pathname.includes('/widget');

const TournamentProfile = ({
  AdditionalTournaments,
}: {
  AdditionalTournaments: () => JSX.Element;
}) => {
  //widget url will contain the tab as param as tab=schedule, use react router to get the tab from the url
  const { tab: tabFromUrl } = useParams();

  const { createDynamicLink } = useDynamicLinks();

  const [tab, setTab] = useState<TournamentTabsEnum>(
    isWidget ? (tabFromUrl as TournamentTabsEnum) : TournamentTabsEnum.DETAILS,
  );
  const [isApprovalQueueOpen, setIsApprovalQueueOpen] = useState(false);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

  const [getTournamentDetails, { isLoading, isFetching }] = useLazyGetTournamentDetailsQuery();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedTournament = useSelector((state) => state.tournamentReducer.selectedTournament);

  const { id, stadiumId, status } = useParams();

  const stadium = useSelector((state) => state.configReducer.selectedStadium);

  const { deleteTournament, loadTournamentSchedule, loadMinimalTeams } = useTournament();

  useEffect(() => {
    loadTournamentDetails();
    loadTournamentSchedule();
    loadMinimalTeams();
  }, [id]);

  const isStartDatePassed = useMemo(() => {
    const startDate = new Date(selectedTournament?.start_date);
    const currentDate = new Date();

    return startDate < currentDate;
  }, [selectedTournament]);

  const loadTournamentDetails = () => {
    getTournamentDetails({ id: Number(id) }).then((res) => {
      dispatch(setSelectedTournament(res.data.data));
    });
  };

  useEffect(() => {
    if (stadium?.id !== Number(stadiumId) && !isWidget) {
      navigate(-1);
    }
  }, [stadium?.id, stadiumId]);

  const tournament = useSelector((state) => state.tournamentReducer.selectedTournament);

  const minimalTournaments = useSelector((state) => state.tournamentReducer.minimalTournaments);

  const { getTranslatedTitle } = useLang();

  const isFederation = usePermissions(FederationsPermissions.TOURNAMENTS_VIEW);

  const renderTabs = useMemo(() => {
    switch (tab) {
      case TournamentTabsEnum.DETAILS:
        return <Details setIsApprovalQueueOpen={setIsApprovalQueueOpen} />;
      case TournamentTabsEnum.ORGANIZERS:
        return <TournamentOrganizers isLoading={isLoading} />;
      case TournamentTabsEnum.TEAMS:
        return <TournamentTeams isLoading={isLoading} />;
      case TournamentTabsEnum.SCHEDULE:
        return <Schedule isLoading={isLoading} AdditionalTournaments={AdditionalTournaments} />;
      case TournamentTabsEnum.EXTRA_MATCHES:
        return <ExtraMatches isLoading={isLoading} AdditionalTournaments={AdditionalTournaments} />;
      case TournamentTabsEnum.STANDINGS:
        return (
          <TournamentStandings
            isLoading={isLoading}
            AdditionalTournaments={AdditionalTournaments}
          />
        );
      case TournamentTabsEnum.POSITIONS:
        return <TournamentPositions isLoading={isLoading} />;
      case TournamentTabsEnum.KNOCKOUTS:
        return <Knockouts AdditionalTournaments={AdditionalTournaments} />;
      default:
        return <Details setIsApprovalQueueOpen={setIsApprovalQueueOpen} />;
    }
  }, [tab, selectedTournament]);

  if (isLoading || isFetching) {
    return <Spinner />;
  }

  return (
    <PageContainer
      title={t('TournamentProfile.title')}
      description={t('TournamentProfile.description')}
    >
      <Grid container>
        <Grid item sm={12}>
          <BlankCard>
            {!isWidget ? (
              <CardMedia
                component="img"
                image={selectedTournament?.tournament_image || profilecover}
                alt={profilecover}
                width="100%"
                height={400}
              />
            ) : null}

            {/* edit and delete buttons */}
            <Stack
              flexDirection={'row'}
              alignItems={'center'}
              position={'absolute'}
              top={5}
              right={5}
            >
              {!isFederation && (
                <Tooltip
                  title={t('TournamentProfile.share')}
                  sx={{ marginRight: 1, backgroundColor: 'white' }}
                >
                  <IconButton
                    className="action-button-for-list"
                    onClick={() => {
                      createDynamicLink({
                        id: selectedTournament?.id,
                        targetType: LinkType.TOURNAMENT,
                      }).then((link) => {
                        navigator.clipboard.writeText(link);

                        toast.success(t('TournamentProfile.linkCopied'));
                      });
                    }}
                  >
                    <IconShare width={20} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('Coaches.edit')} sx={{ marginRight: 1, backgroundColor: 'white' }}>
                <IconButton
                  disabled={status !== TournamentsStatus.ACTIVE}
                  color="info"
                  className="action-button-for-list"
                  onClick={() => {
                    setIsAddFormOpen(true);
                  }}
                >
                  <IconEdit width={20} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={t('Coaches.delete')}
                sx={{ backgroundColor: 'white', marginRight: 1 }}
              >
                <IconButton
                  disabled={isStartDatePassed}
                  onClick={() => {
                    setDeleteAlertOpen(true);
                  }}
                  color="error"
                  className="action-button-for-list"
                >
                  <IconTrash width={20} />
                </IconButton>
              </Tooltip>
            </Stack>
            {/* Search tournaments field */}
            <Stack
              flexDirection={'row'}
              alignItems={'center'}
              position={'absolute'}
              bottom={55}
              left={5}
            >
              <Autocomplete
                multiple={false}
                options={minimalTournaments || []}
                placeholder={t('TournamentProfile.searchTournament')}
                onChange={(_, value) => {
                  if (value?.id) {
                    navigate(`/pages/${stadium?.id}/tournaments/${status}/${value?.id}`);
                  }
                }}
                value={{
                  id: tournament?.id,
                  name: tournament?.name,
                  name_ar: tournament?.name_ar,
                }}
                renderInput={(params) => <TextField {...params} />}
                getOptionLabel={(option) => getTranslatedTitle(option, 'name')}
                sx={{ backgroundColor: 'white', minWidth: 300, borderRadius: "7px" }}
                disableClearable
              />
            </Stack>
            {/*  */}
            {!isWidget ? <TournamentTabs tab={tab} setTab={setTab} /> : null}
          </BlankCard>
        </Grid>
        {renderTabs}
      </Grid>
      {!isWidget && (
        <ApprovalQueue
          open={isApprovalQueueOpen}
          onClose={() => setIsApprovalQueueOpen(false)}
          loadTournamentDetails={loadTournamentDetails}
        />
      )}
      <AddEditTournamentForm
        open={isAddFormOpen}
        onClose={() => {
          setIsAddFormOpen(false);
        }}
        selectedTournament={selectedTournament as unknown as Tournament}
        loadTournaments={loadTournamentDetails}
      />
      <DeleteAlert
        open={deleteAlertOpen}
        handleClose={() => setDeleteAlertOpen(false)}
        title={t('TournamentsScreen.deleteTournament')}
        message={t('TournamentsScreen.deleteTournamentMessage')}
        buttons={[
          {
            text: t('Coaches.delete'),
            color: 'error',
            onClick: () => deleteTournament(selectedTournament?.id),
          },
          {
            text: t('Coaches.cancel'),
            color: 'primary',
            onClick: () => {
              setDeleteAlertOpen(false);
            },
          },
        ]}
      />
    </PageContainer>
  );
};

export default TournamentProfile;
